/**
 * The guided editor tour: a driver.js spotlight walk through the workspace.
 *
 * The module owns the stops and the driver orchestration; App.tsx owns the state.
 * The seam is TourDeps — the three state flips the tour needs (chat open, sidebar open,
 * wizard offer open) plus the "record it as seen" callback.
 *
 * The first two run ONCE, up front, before driving. Neither is conditional on
 * where the walk has got to, both are idempotent, and both are already satisfied in
 * the common case (the sidebar defaults open; the chat panel only needs a
 * conversation). Doing them up front is what keeps every transition on driver.js's
 * own default next/previous handling: an overridden onNextClick that AWAITS anything
 * before calling moveNext() lands after driver's own click/transition cycle has
 * settled, and driver then strips .driver-active-element off whichever element its
 * (asynchronously updated) bookkeeping still believes is current — so the popover's
 * text advances while the spotlight and the popover's positioning anchor stay behind
 * on the previous element, and .driver-no-interaction piles up element by element.
 * Verified live: driver's own default next never does this.
 *
 * The third is the one exception, and it is synchronous: the last stop lives inside a
 * modal dialog, and driver resolves a step's element before running any of that step's
 * hooks, so the dialog has to be opened by the click that leaves the stop before it.
 * That override opens and calls driver's own moveNext in the same tick — no await — so
 * driver's transition is byte-for-byte the one it would have run itself. buildDriveSteps
 * carries the full argument and the source references.
 *
 * Stops reference elements by selector string only, resolved at highlight time:
 * StoryCanvas remounts wholesale on chapter switch (key={activeChapter.id}), so a
 * cached element can die between steps. The one exception is the opening stop, which
 * names no element at all -- see CenteredTourStop.
 */
import { driver, type DriveStep, type PopoverDOM } from 'driver.js'
// Documents this module's dependency on driver's stylesheet; main.tsx also imports it
// (ahead of index.css, deliberately -- see the comment there) so it lands in the bundle
// before index.css's token-based repaint of .driver-popover regardless of import order
// through this module. Vite/ESM dedupes the repeated import, so this doesn't re-inject it.
import 'driver.js/dist/driver.css'

export interface TourDeps {
  /** Open the right panel with an active conversation (handleCreateConversation). */
  ensureChatOpen: () => void
  /** setShowLeftSidebar(true) — the sidebar is unmounted while hidden. */
  ensureSidebarOpen: () => void
  /**
   * setIsWizardOfferOpen(true). Called once, on the click that advances into the last
   * stop, which is the wizard offer itself — the tour ends by handing the writer that
   * choice rather than by describing it. Never called on a story that already has prose:
   * the stop is dropped up front along with the wizard nudge (see WIZARD_NUDGE_SELECTOR).
   */
  openWizardOffer: () => void
  /** Any exit, completed or dismissed: record the tour as seen. */
  onFinished: () => void
}

export interface TourControls {
  moveNext: () => void
  destroy: () => void
}

interface TourStopCommon {
  id: string
  title: string
  description: string
}

/** A stop that highlights a real element already on the page. */
export interface AnchoredTourStop extends TourStopCommon {
  selector: string
  side: 'top' | 'right' | 'bottom' | 'left'
  align: 'start' | 'center' | 'end'
  /** Dropped silently at start time when its element is absent (conditional UI). */
  optional?: boolean
  /**
   * What an `optional` stop's presence is actually decided by, when that is something
   * other than its own target. Only the wizard-offer stop needs it: its target lives
   * inside a dialog the tour has not opened yet, so its own selector matches nothing at
   * start time and it would always be dropped. Keying it on the wizard nudge is not a
   * convenience — it is the requirement that this stop appear on exactly the stories the
   * nudge appears on (empty ones), because the wizard writes an opening scene.
   */
  presenceSelector?: string
  /**
   * Set on the one stop that lives INSIDE a native <dialog> the tour opens for it.
   *
   * A dialog opened with showModal() sits in the browser's top layer and makes the rest
   * of the document inert, so a driver popover left in <body> for such a stop is painted
   * behind the dialog AND unclickable — measured: elementFromPoint at its own Next button
   * returned <html>, and a programmatic .click() did not fire the button's listener. This
   * selector is both the flag ("this stop is inside a dialog") and the reparent target:
   * the popover is moved into that dialog so it escapes the inertness, and the highlight
   * becomes a CSS ring on .driver-active-element because driver's SVG overlay cannot
   * paint above the top layer either. See buildDriveSteps and index.css.
   */
  dialogSelector?: string
}

/**
 * A stop with nothing to highlight — driver.js's own idiomatic "welcome step" shape.
 * There is exactly one of these, TOUR_STOPS[0]: the tour's introduction, a centered card
 * that opens the walk before anything is spotlighted. side/align describe how a popover
 * anchors to an element, which this stop doesn't have, so they're left out rather than
 * given values nothing reads — and because this interface has no `selector` field at
 * all, there's no empty string or stray literal for a future stop to fall into by
 * accident; buildDriveSteps can only omit `element` for a stop shaped exactly like this
 * one (see isAnchoredTourStop).
 *
 * Confirmed against driver's own source (node_modules/driver.js/dist/driver.js.mjs,
 * functions `m`, `f`, and `K`): whether driver arms its MutationObserver wait is decided
 * in `m` by `i.element`'s truthiness, so `element: undefined` and an omitted `element`
 * key behave identically there — both falsy, both skip the wait, both fall straight
 * through to a synchronous highlight. But the element actually used for that highlight is
 * resolved by `K`, unconditionally, via `f(t.element)` — and `f` dispatches on `typeof`,
 * not truthiness: `typeof '' === 'string'` too, so `element: ''` reaches
 * `document.querySelector('')`, which throws a SyntaxError synchronously. `undefined`
 * never reaches that branch (`f` just hands it back), so it resolves to driver's own
 * centered dummy element instead, with no throw and no wait. A syntactically valid but
 * unmatched selector (a typo, a not-yet-mounted element with no `waitForElement` cover)
 * is the one shape that actually pays the full 3s `waitForElement` cost, because that
 * step's truthiness check passes while `f`'s `querySelector` still comes back null. Net:
 * omitted-or-undefined centers immediately; '' throws; an unmatched string hangs for 3s.
 * buildDriveSteps omits the key outright so this stop can't drift into either broken shape.
 */
export type CenteredTourStop = TourStopCommon

export type TourStop = AnchoredTourStop | CenteredTourStop

/** True for a stop that names a real element to highlight. */
export function isAnchoredTourStop(stop: TourStop): stop is AnchoredTourStop {
  return 'selector' in stop
}

/**
 * The wizard nudge decides two stops, not one: the nudge stop itself and the wizard-offer
 * dialog stop that ends the tour. App.tsx renders the nudge only while the manuscript is
 * empty, and the offer must follow exactly that rule — popping the wizard offer at the end
 * of a tour on a story that already has prose is precisely what the app avoids elsewhere.
 */
const WIZARD_NUDGE_SELECTOR = '.wizard-nudge'

/**
 * The manuscript stop's own target, factored out because App.tsx's manual "Take the
 * tour" entry point needs it too: that entry switches to the canvas tab itself and has
 * to wait for the workspace to actually mount before starting the tour, so it waits on
 * this selector rather than a re-typed literal that could drift from the stop it names.
 */
export const MANUSCRIPT_SELECTOR = '.manuscript-surface'

export const TOUR_STOPS: TourStop[] = [
  {
    id: 'welcome',
    title: 'Introduction to the editor',
    description:
      'Welcome to the editor. This short tour walks through your manuscript, your co-writer, and the rest of the workspace you will be writing in.'
  },
  {
    id: 'manuscript',
    // The whole writing surface -- the <section> wrapping both the toolbar and the
    // scrolling page -- so the walk lands here first among real elements, and the next
    // stop narrows to the toolbar inside it.
    selector: MANUSCRIPT_SELECTOR,
    title: 'Your manuscript',
    description:
      'This is where your story lives — the page, and the tools above it. Write directly here; everything saves as you type.',
    side: 'right',
    align: 'center'
  },
  {
    id: 'toolbar',
    selector: '.manuscript-formatting-toolbar',
    title: 'Formatting toolbar',
    description:
      'Undo, redo, bold, italics, lists, quotes — everything for shaping the text lives up here.',
    side: 'bottom',
    align: 'start'
  },
  {
    id: 'co-writer',
    selector: '.chat-composer-shell .astryx-chat-composer',
    title: 'Meet your co-writer',
    description:
      'The heart of 9nights: an agent that knows your story and its wiki. Ask it to draft a scene, rework a paragraph, or figure out what happens next.',
    side: 'left',
    align: 'center'
  },
  {
    id: 'modes',
    selector: '[role="radiogroup"][aria-label="Conversation mode"]',
    title: 'Writing and Ask',
    description:
      'Writing mode drafts prose with you. Ask mode answers questions about your story without touching the manuscript.',
    side: 'left',
    align: 'end'
  },
  {
    id: 'plan-mode',
    selector: '.chat-composer-mode-row .astryx-segmented-control-item[data-value="plan"]',
    title: 'Plan mode',
    description:
      'Plan before you write: the agent lays out an outline you can comment on and refine. A story that starts with a plan usually ends up a better one.',
    side: 'left',
    align: 'end'
  },
  {
    id: 'new-chapter',
    selector: '[data-tour="new-chapter"]',
    title: 'New chapter',
    description: 'Split your story into chapters as it grows. Each chapter keeps its own history.',
    side: 'bottom',
    align: 'end'
  },
  {
    id: 'publish',
    selector: '[data-tour="publish"]',
    title: 'Publish',
    description:
      "When you're ready for readers, publish your story to the 9nights library from here.",
    side: 'bottom',
    align: 'end'
  },
  {
    id: 'settings-button',
    selector: '.workspace-header-actions button[aria-label="Story settings"]',
    title: 'Story settings',
    description:
      'Your story title, its genres, and the blurb readers see all live in here, along with your daily goal, your chapters, and export.',
    side: 'bottom',
    align: 'end'
  },
  {
    id: 'nav-new-story',
    selector: '[data-testid="tour-nav-new-story"]',
    title: 'New story',
    description:
      'Start something fresh any time — each story gets its own manuscript, wiki, and chats.',
    side: 'right',
    align: 'start'
  },
  {
    id: 'nav-story-wiki',
    selector: '[data-testid="tour-nav-story-wiki"]',
    title: 'Story wiki',
    description:
      'Characters, places, and lore — kept in sync with your manuscript as you write, and read by the agent.',
    side: 'right',
    align: 'start'
  },
  {
    id: 'nav-recent-stories',
    selector: '[data-tour="recent-stories"]',
    title: 'Recent stories',
    description: 'Your latest work, one click away.',
    side: 'right',
    align: 'start'
  },
  {
    id: 'nav-my-stories',
    selector: '[data-testid="tour-nav-my-stories"]',
    title: 'My stories',
    description: 'The full shelf — everything you have written, in one place.',
    side: 'right',
    align: 'start'
  },
  {
    id: 'nav-account',
    selector: '.account-menu-footer .account-menu-trigger',
    title: 'Your account',
    description:
      "Account settings, your profile, and the app's theme live here — and so does this tour, any time you want to take it again.",
    side: 'right',
    align: 'end'
  },
  {
    id: 'nav-guided-wizard',
    selector: WIZARD_NUDGE_SELECTOR,
    title: 'Guided wizard',
    description:
      'Prefer a guided start? The wizard asks a few questions and turns your answers into a story bible and an opening scene.',
    side: 'right',
    align: 'end',
    optional: true
  },
  {
    id: 'wizard-offer',
    // Only matches once the dialog is actually modal: Astryx's Dialog calls showModal()
    // from a passive effect, a tick after React commits the markup, and [open] is what
    // that call sets. driver's own waitForElement observes the DOM until this resolves,
    // so keying on [open] is what makes the popover render into a dialog that is really
    // there rather than into a display:none one.
    selector: '.wizard-offer-dialog[open] .wizard-offer-card',
    title: 'Ready when you are',
    description:
      "Here it is — take the guided start, or close this and write the first line yourself. Either way, you're set. Enjoy the writing.",
    side: 'bottom',
    align: 'center',
    optional: true,
    presenceSelector: WIZARD_NUDGE_SELECTOR,
    dialogSelector: '.wizard-offer-dialog'
  }
]

/**
 * The tour now takes precedence over the wizard-offer modal: an unseen tour auto-starts
 * without waiting for the offer to be dismissed first. This is a deliberate inversion of
 * the original order -- the tour's own last stop (the 'wizard-offer' stop in TOUR_STOPS)
 * opens and spotlights that same dialog, so the offer simply moves from before the tour to
 * after it rather than being lost. The full-screen wizard (wizardOpen) is different: it
 * genuinely replaces the workspace, so the tour still yields to that.
 */
export function shouldAutoStartTour(state: {
  /** undefined = the settings round-trip has not resolved yet. */
  hasSeenEditorTour: boolean | undefined
  workspaceReady: boolean
  wizardOpen: boolean
  alreadyRanThisSession: boolean
}): boolean {
  return (
    state.hasSeenEditorTour === false &&
    state.workspaceReady &&
    !state.wizardOpen &&
    !state.alreadyRanThisSession
  )
}

const WAIT_POLL_MS = 100

/**
 * Bounded poll for a selector. Resolves null on timeout — callers advance anyway.
 *
 * Used by App.tsx's manual "Take the tour" entry point to defer startEditorTour until
 * the manuscript has actually mounted. Nothing inside a running tour uses this: any
 * mid-walk wait is driver's own `waitForElement`, which happens inside its transition
 * rather than around it (see startEditorTour).
 */
export function waitFor(selector: string, timeoutMs = 3000): Promise<Element | null> {
  return new Promise((resolve) => {
    const startedAt = Date.now()
    const poll = (): void => {
      const found = document.querySelector(selector)
      if (found) return resolve(found)
      if (Date.now() - startedAt >= timeoutMs) return resolve(null)
      setTimeout(poll, WAIT_POLL_MS)
    }
    poll()
  })
}

/** Ceiling on the wait for the dialog's entry animation before the popover moves in. */
const DIALOG_SETTLE_MS = 1000

/**
 * Moves the popover into the dialog it belongs to, once that dialog has stopped moving.
 *
 * Why into the dialog: showModal() puts the <dialog> in the top layer and makes the rest
 * of the document inert. A popover left in <body> is painted underneath it and cannot be
 * clicked at all — not by a user, not by dispatchEvent. Reparenting is the only way to
 * put the tour's own Done button back within reach.
 *
 * Why not immediately: driver positions the popover right AFTER this hook returns (in
 * driver.js's `x`, `onRender` is called and only then is the popover positioned), and
 * Astryx's dialog animates in with a transform. A transformed ancestor becomes the
 * containing block for `position: fixed`, so positioning while the popover sits inside a
 * mid-animation dialog resolves driver's viewport coordinates against the dialog's own
 * box instead — off by the dialog's origin, permanently. Letting driver position it in
 * <body> first (viewport coordinates, correct) and moving it once the animation has
 * finished (animation-fill-mode is `backwards`, so the transform is gone by then) keeps
 * the inline left/top it computed meaning exactly what it meant before the move.
 */
function reparentIntoDialog(popover: PopoverDOM, dialogSelector: string): void {
  const dialog = document.querySelector(`${dialogSelector}[open]`)
  if (!dialog) return

  const move = (): void => {
    if (popover.wrapper.parentElement !== dialog) dialog.appendChild(popover.wrapper)
  }

  // No animation to wait out (jsdom, reduced motion, an already-settled dialog): move now,
  // before driver positions, which is fine precisely because there is no transform.
  const entry = typeof dialog.getAnimations === 'function' ? dialog.getAnimations() : []
  if (entry.length === 0) {
    move()
    return
  }

  // The timeout is a floor, not a race to win: a move that lands mid-animation is
  // self-correcting (the popover keeps its own fixed coordinates once the transform
  // goes away), so the worst case is a moment of visible offset, not a stuck popover.
  void Promise.race([
    Promise.allSettled(entry.map((animation) => animation.finished)),
    new Promise((resolve) => window.setTimeout(resolve, DIALOG_SETTLE_MS))
  ]).then(move)
}

/** What the one dialog stop needs from its host to be reachable at all. */
export interface DialogStopWiring {
  /** Opens the dialog that stop lives inside (TourDeps.openWizardOffer). */
  openDialog: () => void
  /** driver's own moveNext, called synchronously in the same click tick. */
  moveNext: () => void
}

/**
 * One drive step per stop: a straight translation of the stop data, and — for the single
 * stop marked with a `dialogSelector` and the one immediately before it — the three hooks
 * that stop needs. Every other step still carries no lifecycle hooks at all, so driver's
 * own default next/previous handling drives every other transition in both directions.
 *
 * The sequencing here is the load-bearing part, and it is dictated by driver.js's source
 * (`node_modules/driver.js/dist/driver.js.mjs`, function `m`):
 *
 *   m(index) resolves the step's element and, if it is missing, parks on a
 *   MutationObserver — BOTH before any hook of that step runs.
 *
 * So a dialog stop cannot open its own dialog: by the time `onHighlightStarted` fires,
 * driver has already decided the element does not exist and fallen back to its centred
 * dummy. The dialog has to be open, or at least opening, before the move begins — which
 * makes the click that leaves the previous stop the only place it can happen. That click
 * runs while the document is still interactive, so it is safe; the override is strictly
 * synchronous (open, then driver's own moveNext, no await), which is what keeps it
 * equivalent to driver's default `() => steps[i+1] ? m(i+1) : destroy()` rather than the
 * deleted await-then-moveNext path that stranded the spotlight a step behind.
 *
 * React commits the dialog a tick later; driver's own `waitForElement` bridges that gap
 * from inside its move, where its highlight bookkeeping stays consistent.
 */
export function buildDriveSteps(stops: TourStop[], wiring: DialogStopWiring): DriveStep[] {
  return stops.map((stop, index) => {
    const step: DriveStep = {
      popover: {
        title: stop.title,
        description: stop.description
      }
    }

    // Anchored stops get a real `element` plus the side/align that positions the popover
    // against it. Centered stops (the welcome card) get neither key at all -- not even
    // `element: undefined` -- which is what makes driver take its immediate centered-popover
    // path instead of arming its 3s waitForElement MutationObserver. See CenteredTourStop.
    if (isAnchoredTourStop(stop)) {
      step.element = stop.selector
      step.popover!.side = stop.side
      step.popover!.align = stop.align
    }

    const nextStop = stops[index + 1]
    if (nextStop && isAnchoredTourStop(nextStop) && nextStop.dialogSelector) {
      step.popover!.onNextClick = () => {
        wiring.openDialog()
        wiring.moveNext()
      }
    }

    const dialogSelector = isAnchoredTourStop(stop) ? stop.dialogSelector : undefined
    if (dialogSelector) {
      step.popover!.onPopoverRender = (popover) => reparentIntoDialog(popover, dialogSelector)
      // Terminal by construction. Stepping back out of a modal dialog would render the
      // previous stop's popover into a document the open dialog has made inert -- an
      // unclickable dead end. showButtons drops the button; onPrevClick swallows the key,
      // because driver's ArrowLeft handler goes straight to the step's onPrevClick and
      // never consults showButtons.
      step.popover!.showButtons = ['next', 'close']
      step.popover!.onPrevClick = () => {}
    }

    return step
  })
}

/**
 * Starts the tour and returns a handle to end it early.
 *
 * The tour is not tied to React's lifecycle at all -- driver.js's overlay/popover DOM lives
 * purely in this closure, torn down only through the tour's own exit paths (Next-to-completion,
 * Close click, Escape). None of those fire if the host removes the tour's anchors out from
 * under it instead (a caller can watch for that -- e.g. the workspace unmounting mid-tour --
 * and call the returned `destroy()`, which is the exact same path Escape and Close already
 * use, so it records the tour as seen like any other dismissal rather than needing a separate
 * exit).
 */
export function startEditorTour(deps: TourDeps): TourControls {
  // Both preconditions, once, before anything is highlighted: the co-writer stop needs
  // the chat panel open and the nav stops need the sidebar mounted. Both are idempotent
  // and usually already true, and React commits them long before the walk reaches those
  // stops (they sit three and eight clicks in). Doing them here rather than per-step is
  // what lets every transition stay on driver's own default next -- see the module comment.
  deps.ensureSidebarOpen()
  deps.ensureChatOpen()

  // Optional stops with no element right now (e.g. the wizard nudge once prose
  // exists) are dropped up front — cheaper and more predictable than skipping live.
  // This reads the DOM before React has committed ensureSidebarOpen, so a writer who
  // had explicitly hidden the sidebar loses the (optional, sidebar-hosted) wizard stop
  // for that run. showLeftSidebar defaults to true, so the default path is unaffected,
  // and losing one optional stop beats highlighting an element that isn't there.
  //
  // presenceSelector is what ties the wizard-offer stop to the same condition as the
  // nudge: its own target is inside a dialog that does not exist yet, so testing that
  // would drop it every time and, worse, testing nothing would open the wizard offer at
  // the end of a tour on a story that already has prose.
  const stops = TOUR_STOPS.filter(
    (stop) =>
      !isAnchoredTourStop(stop) ||
      !stop.optional ||
      document.querySelector(stop.presenceSelector ?? stop.selector)
  )

  // Built ahead of `tour` (referenced through it only via closures below, never called until
  // after `tour` is assigned) so the same object can be the handle this function returns.
  const controls: TourControls = {
    moveNext: () => tour.moveNext(),
    destroy: () => tour.destroy()
  }

  const tour = driver({
    showProgress: true,
    overlayOpacity: 0.6,
    disableActiveInteraction: true,
    // driver's default is 10, which the first stop cannot afford: .manuscript-surface sits
    // in a 5px inset inside .canvas-wrapper, so a 10px cutout spilled 5px past the column
    // on all four sides -- measured, stage x235..965 y35..725 against a surface of
    // x245..955 y45..715 in a wrapper of x240..960 y40..720. 4 keeps the cutout inside the
    // column with a pixel to spare while still reading as a halo on the small targets
    // (nav rows, header buttons) the rest of the walk points at. It also tightens the
    // popover's own gap, which driver computes as stagePadding + popoverOffset.
    stagePadding: 4,
    // driver's own default set, stated explicitly because it is a product decision:
    // the walk is navigable in both directions. Nothing is prepared per-step any more,
    // so a step back needs no undo -- driver just re-highlights the previous anchor.
    showButtons: ['next', 'previous', 'close'],
    // Purely pacing: the sliding-stage animation adds driver's `duration` (400ms) of
    // visible lag to every transition in the walk, which reads as sluggish rather than
    // smooth when you are clicking straight through.
    animate: false,
    // The one place the up-front preconditions can still be caught out. ensureChatOpen
    // flips the panel on synchronously but the composer only mounts once the new
    // conversation has been saved (App.tsx's handleCreateConversation awaits that), so a
    // user who clicks Next twice inside that round-trip can reach the co-writer stop
    // before its anchor exists — driver falls back to a centred, spotlight-less popover.
    // driver's own bounded wait fixes it: when a step's element is missing it observes
    // the DOM (MutationObserver, plus this timeout as the floor) and re-enters its own
    // transition once the element appears. Crucially the wait happens INSIDE driver's
    // move, so its highlight bookkeeping stays consistent — unlike awaiting in a popover
    // hook and then calling moveNext() from outside, which is what stranded the spotlight
    // on the previous element. Applies to backward navigation too, for free.
    // Also what bridges React's commit of the wizard-offer dialog: the last stop's target
    // only exists once that dialog is open, and this is the wait that covers the gap
    // between the click that opens it and the element appearing. See buildDriveSteps.
    waitForElement: 3000,
    onDestroyed: () => deps.onFinished(),
    steps: buildDriveSteps(stops, {
      openDialog: deps.openWizardOffer,
      moveNext: () => controls.moveNext()
    })
  })
  tour.drive()
  return controls
}
